import React from 'react';

import styled from '@emotion/styled';
import { Button } from '@lightspeed/flame/Button';
import { Flex } from '@lightspeed/flame/Core';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@lightspeed/flame/Modal';
import { Text } from '@lightspeed/flame/Text';

import ManualPayment from 'payments/ManualPayment';

interface OptInModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  onComplete: () => void;
  swapStepTwoButtonOrder?: boolean;
  isFromReportPage?: boolean;
  closeShouldCloseAllModals?: boolean;
}

const StyledModal = styled(Modal)`
  width: 600px;
`;

const StyledBodyText = styled(Text)`
  font-size: 14px;
  line-height: 18px;
`;

const StyledModalBody = styled(ModalBody)`
  padding-top: 22px;
  padding-bottom: 22px;
`;

const OptInModal: React.FC<OptInModalProps> = ({
  isOpen,
  onRequestClose,
  onComplete,
  swapStepTwoButtonOrder = false,
  isFromReportPage = false,
  closeShouldCloseAllModals = false,
}) => {
  const [step, setStep] = React.useState(1);

  React.useEffect(() => {
    if (isOpen && !isFromReportPage) {
      window.lsCloud.Options.get('customer_acknowledged_card_on_file').done((acknowledged) => {
        if (acknowledged !== '1') {
          window.lsCloud.Options.save('customer_acknowledged_card_on_file', '1');
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isFromReportPage]);

  const closeButtonAction = () => {
    if (closeShouldCloseAllModals) {
      ManualPayment.dismissAllModals();
    } else {
      onRequestClose();
    }
  };

  return (
    <StyledModal isOpen={isOpen} data-test="opt-in-modal">
      <ModalHeader
        showCloseButton={true}
        onCloseClicked={closeButtonAction}
        data-test="opt-in-header"
      >
        <Text>
          {step === 1 ? 'Terms of use for Saved Payment Method' : 'Employee Role Permissions'}
        </Text>
      </ModalHeader>
      <StyledModalBody data-test="opt-in-body">
        <Flex>
          {step === 1 ? (
            <StyledBodyText>
              By clicking &lsquo;Accept&rsquo; below, you are enabling the Saved Payment Method
              feature. This will allow you to store your customer&apos;s payments method for future
              use. You accept that all transactions processed through this feature will be subject
              to your Card-Not-Present processing rate. Lightspeed hereby expressly disclaims any
              and all liability related to any fraudulent, failed or rejected transactions or any
              chargebacks made while using the Saved Payment Method feature.
            </StyledBodyText>
          ) : (
            <StyledBodyText>
              Your employees can’t access this feature. To enable it, go to Settings &gt; Employee
              Roles and turn on the following permissions:
              <ul>
                <li>Sales - Use customer’s saved payment method</li>
                <li>Customers - Add saved payment method</li>
                <li>Customers - Edit saved payment method</li>
              </ul>
            </StyledBodyText>
          )}
        </Flex>
      </StyledModalBody>
      <ModalFooter>
        {step === 1 ? (
          <Flex justifyContent="flex-end">
            <Button
              data-test="opt-in-modal-cancel-button"
              mr="12px"
              variant="neutral"
              fill={true}
              onClick={() => {
                onRequestClose();
              }}
            >
              Cancel
            </Button>
            <Button
              data-test="opt-in-modal-accept-button"
              variant="secondary"
              fill={true}
              onClick={() => {
                window.lsCloud.Options.save('customer_use_card_on_file', '1')
                  .done(() => {
                    onComplete();
                    setStep(2);
                  })
                  .fail(() => {
                    window.showNotifier(
                      'warning',
                      'Failed to opt into Saved Payment Methods, please try again later.'
                    );
                  });
              }}
            >
              Accept &amp; edit permissions
            </Button>
          </Flex>
        ) : (
          <Flex justifyContent="flex-end">
            {swapStepTwoButtonOrder ? (
              <>
                <Button
                  variant="neutral"
                  fill={true}
                  mr="12px"
                  onClick={() => {
                    ManualPayment.dismissAllModals();
                    window.navigateToContent('listing', 'admin.listings.employee_roles');
                  }}
                >
                  Edit Employee Roles
                </Button>
                <Button variant="secondary" fill={true} onClick={onRequestClose}>
                  Continue
                </Button>
              </>
            ) : (
              <>
                <Button variant="neutral" fill={true} mr="12px" onClick={onRequestClose}>
                  Later
                </Button>
                <Button
                  variant="secondary"
                  fill={true}
                  onClick={() => {
                    window.navigateToContent('listing', 'admin.listings.employee_roles');
                  }}
                >
                  Edit Employee Roles
                </Button>
              </>
            )}
          </Flex>
        )}
      </ModalFooter>
    </StyledModal>
  );
};

export default OptInModal;
